import { ReactNode, useCallback } from 'react';
import { useSelector } from 'react-redux';

// Utils
import { selectIsDesktop } from '~/components/brand/ducks/selectors';
import Icon from '~/components/icon';

// Hooks
import useLockCurrentContextScroll from '~/hooks/use-lock-current-context-scroll';

// Components
import { H2, H3 } from '@comeon/ui';

// Styled components
import {
	ActionButton,
	CloseButton,
	ContentActions,
	ContentDetails,
	ContentText,
	ContentWrapper,
	Header
} from '~/components/payment/suspended-players-modal/content/styles';

export type ActionButtonProps = {
	text: string;
	onClick: () => void;
	iconType?: string;
	className?: string;
	testId?: string;
};

export type PopupProps = {
	onClose: () => void;
	title: string;
	content: string | ReactNode;
	primaryAction?: ActionButtonProps;
	secondaryAction?: ActionButtonProps;
	testId?: string;
	center: boolean;
};

const SuspendedPlayerModalContent = ({
	center,
	onClose,
	title,
	content,
	primaryAction,
	secondaryAction,
	testId = 'shared-popup'
}: PopupProps) => {
	const isDesktop = useSelector(selectIsDesktop);

	const handleCloseButtonClick = useCallback(() => {
		onClose();
	}, [onClose]);

	// Lock scroll when popup is open
	useLockCurrentContextScroll();

	return (
		<ContentWrapper isDesktop={isDesktop} center={center}>
			<Header>
				<H2>{title}</H2>
				<CloseButton
					className="at-icon-close page-head__close"
					onClick={handleCloseButtonClick}
					type="button"
					data-testid={`${testId}-close-button`}
				/>
			</Header>
			<ContentDetails>
				{typeof content === 'string' ? <ContentText>{content}</ContentText> : content}
			</ContentDetails>
			{(primaryAction || secondaryAction) && (
				<ContentActions>
					{primaryAction && (
						<ActionButton
							className={primaryAction.className || 'btn-primary'}
							type="button"
							onClick={primaryAction.onClick}
							data-testid={primaryAction.testId || `${testId}-primary-action`}
						>
							{primaryAction.iconType && <Icon type={primaryAction.iconType} />}
							<H3>{primaryAction.text}</H3>
						</ActionButton>
					)}
					{secondaryAction && (
						<ActionButton
							className={secondaryAction.className || 'btn-secondary'}
							type="button"
							onClick={secondaryAction.onClick}
							data-testid={secondaryAction.testId || `${testId}-secondary-action`}
						>
							{secondaryAction.iconType && <Icon type={secondaryAction.iconType} />}
							<H3>{secondaryAction.text}</H3>
						</ActionButton>
					)}
				</ContentActions>
			)}
		</ContentWrapper>
	);
};

export default SuspendedPlayerModalContent;
